import { CurrentUserContextType, useToastProvider } from '@qualio/ui-components';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { EventType, VersionedTemplateStatusValues } from '../../../types';
import eventTypesApi from '../../../api/eventTypes.api';
import templatesApi from '../../../api/templates.api';
import { AxiosError } from 'axios';
import { extractMessageFromError } from '../../../utils/errorUtils';
import { useNavigate } from 'react-router-dom';
import { baseEventTemplateQueryKey } from '../../../hooks';

export const BASE_QUERY_KEY = 'eventTypesList';

type CompanyId = CurrentUserContextType['companyId'];
type TemplateId = EventType['id'];
type LineageId = EventType['lineage_id'];

export const useGetTemplates = (companyId: CompanyId, status: VersionedTemplateStatusValues) => {
  return useQuery({
    queryKey: [BASE_QUERY_KEY, companyId, status],
    queryFn: () => eventTypesApi.getAllEventTypes(companyId, status),
  });
};

export const useCreateNewDraftTemplate = (companyId: CompanyId) => {
  const { showToast } = useToastProvider();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (lineageId: LineageId) => templatesApi.createNewDraftTemplate(companyId, lineageId),
    onSuccess: (data) => {
      showToast({
        title: 'New draft created',
        description: 'A new draft of the event template has been created',
        status: 'success',
        'data-cy': 'qe-event-template-create-draft-success-toast',
      });

      navigate(`/templates/${data.id}`);
    },
    onError: (error: AxiosError) => {
      const defaultMessage = 'Error occurred while creating new draft of the event template';
      const errorMessage = extractMessageFromError(error);
      const message = error?.response?.status === 400 ? errorMessage : defaultMessage;
      showToast({
        title: 'Creating new draft failed',
        description: message,
        status: 'error',
      });
    },
  });
};

export const useArchiveTemplate = (companyId: CompanyId) => {
  const queryClient = useQueryClient();
  const { showToast } = useToastProvider();

  return useMutation({
    mutationFn: (templateId: TemplateId) => templatesApi.archiveTemplate(companyId, templateId),
    onSuccess: (templateId) => {
      queryClient.invalidateQueries([BASE_QUERY_KEY, companyId]);
      queryClient.invalidateQueries([baseEventTemplateQueryKey, companyId, Number(templateId)]);

      showToast({
        title: 'Template archived',
        description: 'Template has been archived',
        status: 'success',
      });
    },
    onError: (error: AxiosError) => {
      const message = extractMessageFromError(error);
      showToast({
        title: 'Template archiving failed',
        description: message,
        status: 'error',
      });
    },
  });
};

export const useRestoreTemplate = (companyId: CompanyId) => {
  const queryClient = useQueryClient();
  const { showToast } = useToastProvider();

  return useMutation({
    mutationFn: (templateId: TemplateId) => templatesApi.restoreTemplate(companyId, templateId),
    onSuccess: (templateId) => {
      queryClient.invalidateQueries([BASE_QUERY_KEY, companyId]);
      queryClient.invalidateQueries([baseEventTemplateQueryKey, companyId, Number(templateId)]);

      showToast({
        title: 'Template restored',
        description: 'Template has been restored',
        status: 'success',
      });
    },
    onError: () => {
      showToast({
        title: 'Template restoring failed',
        description: 'Error occurred while restoring template',
        status: 'error',
      });
    },
  });
};
